import { Injectable } from '@angular/core';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface ChatbotState {
  messages: Message[];
  sessionId: string;
}

@Injectable({
  providedIn: 'root'
})
export class ChatbotStateService {
  private readonly STORAGE_KEY = 'chatbot_state';
  private state: Partial<ChatbotState> | null = null;

  constructor() {
    this.loadState();
  }

  /** Only persist chat history and session id — never auth secrets. */
  private pickPersistedFields(state: Partial<ChatbotState>): Partial<ChatbotState> {
    const picked: Partial<ChatbotState> = {};
    if (state.messages !== undefined) {
      picked.messages = state.messages;
    }
    if (state.sessionId !== undefined) {
      picked.sessionId = state.sessionId;
    }
    return picked;
  }

  private stripLegacyFields(parsed: Record<string, unknown>): Partial<ChatbotState> {
    const messages = parsed.messages;
    const sessionId = parsed.sessionId;
    const result: Partial<ChatbotState> = {};
    if (Array.isArray(messages)) {
      result.messages = messages as Message[];
    }
    if (typeof sessionId === 'string') {
      result.sessionId = sessionId;
    }
    return result;
  }

  saveState(state: Partial<ChatbotState>): void {
    this.state = {
      ...this.state,
      ...this.pickPersistedFields(state),
    };

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Failed to save chatbot state:', error);
    }
  }

  loadState(): Partial<ChatbotState> | null {
    try {
      const savedState = localStorage.getItem(this.STORAGE_KEY);
      if (savedState) {
        const parsed = JSON.parse(savedState) as Record<string, unknown>;
        const hadLegacyAuth = ['password', 'bearerToken', 'username', 'authMethod', 'bureauId']
          .some((key) => parsed[key] !== undefined && parsed[key] !== null && parsed[key] !== '');
        this.state = this.stripLegacyFields(parsed);
        if (this.state?.messages) {
          this.state.messages = this.state.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp),
          }));
        }
        if (hadLegacyAuth) {
          localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.state));
        }
        return this.state;
      }
    } catch (error) {
      console.error('Failed to load chatbot state:', error);
    }
    return null;
  }

  getState(): Partial<ChatbotState> | null {
    return this.state;
  }

  clearState(): void {
    this.state = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear chatbot state:', error);
    }
  }

  saveMessages(messages: Message[]): void {
    this.saveState({ messages });
  }

  getMessages(): Message[] {
    return this.state?.messages || [];
  }

  saveSessionId(sessionId: string): void {
    this.saveState({ sessionId });
  }

  getSessionId(): string | null {
    return this.state?.sessionId || null;
  }
}
